import React from "react";
import PropTypes from "prop-types";
import JournalLinesTable from "./JournalLinesTable";
import { ReadSource } from "./journalUi";

/**
 * One ERP journal of the request — "Invoice journal - API" or "Payment journal
 * - APP": its number and state, what AX says and its lines (Update in ERP is on the toolbar,
 *   for every journal at once).
 * Creating is one button for every journal, on the toolbar of the tab. A journal
 * is created ONCE: the number ERP returned is the record and is never replaced.
 */
const JournalSection = ({
  section, lines, values, requestId, accountTypes, taxGroups, createOnly, busy,
  onChange, onAddLine, onRemoveLine, header, footer, counterparty, removedLines = [], onRestoreLine,
}) => {
  const perms = section.permissions || {};
  const createOnlyChanges = section.pending?.create_only || [];
  const pending = [...(section.pending?.patch || []), ...createOnlyChanges];
  const num = section.journal?.journal_num;

  return (
    <section className="payment-journal-section" aria-label={section.label}>
      <header className="payment-journal-section-head">
        {/* The tab carries the journal's name and state; the block says which AX journal it is. */}
        <div className="payment-journal-section-title-row">
          <span className="payment-journal-section-num">{num || "Not in ERP yet"}</span>
          {num && section.journal?.created_at && (
            <span className="payment-journal-section-recorded" title="Journal number returned by ERP — created once, never replaced">
              <i className="bi bi-lock-fill" aria-hidden="true" /> Recorded {section.journal.created_at}
              {section.journal.created_by_name ? ` · ${section.journal.created_by_name}` : ""}
            </span>
          )}
          <ReadSource read={section.read} />
        </div>
      </header>

      {header}

      {!num && section.last_error && (
        <div role="alert" className="tw-rounded-geist-sm tw-border tw-border-solid tw-border-[#fecaca] tw-bg-[#fef2f2] tw-px-4 tw-py-2.5 tw-text-[13px] tw-text-[#b91c1c]">
          Last attempt to create it failed: {section.last_error}
        </div>
      )}
      {(section.problems || []).map((p) => (
        <div key={p} className="tw-rounded-geist-sm tw-border tw-border-solid tw-border-[#f7e2c0] tw-bg-[#fff8ee] tw-px-4 tw-py-2.5 tw-text-[13px] tw-text-[#a05e03]">{p}</div>
      ))}
      {(section.warnings || []).map((warning, index) => (
        <p key={index} role="note" className="tw-m-0 tw-text-[13px] tw-text-geist-warning">
          ERP: {typeof warning === "string" ? warning : JSON.stringify(warning)}
        </p>
      ))}
      {section.state === "locked" && (
        <p className="tw-m-0 tw-text-[13px] tw-text-geist-fg-3">
          Posted in AX or the payment is already at the bank — correct it with a reversing journal in AX.
        </p>
      )}

      {(lines.length > 0 || perms.allow_edit_lines) && (
        <JournalLinesTable requestId={requestId} journalType={section.name} lines={lines} values={values}
          erpLines={section.erp_lines || undefined} pending={pending} createOnly={createOnly}
          editable={!!perms.allow_refill && !busy} accountTypes={accountTypes} taxGroups={taxGroups}
          allowEditLines={!!perms.allow_edit_lines} onAddLine={onAddLine} onRemoveLine={onRemoveLine} onChange={onChange}
          counterparty={counterparty} />
      )}

      {footer}

      {removedLines.length > 0 && (
        <div className="payment-journal-removed" aria-label="Removed lines">
          <span className="payment-journal-removed-title">Removed from this journal:</span>
          {removedLines.map((l) => (
            <span key={l.key} className="payment-journal-removed-item">
              {l.label || l.account_type}
              {perms.allow_edit_lines && (
                <button type="button" onClick={() => onRestoreLine(l.key)} disabled={!!busy}
                  aria-label={`Restore ${l.label || l.account_type}`}>Restore</button>
              )}
            </span>
          ))}
        </div>
      )}

      {section.state === "on_erp" && pending.length > 0 && (
        <p className="tw-m-0 tw-text-[13px] tw-text-geist-fg-2">
          <span className="tw-inline-block tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-geist-accent tw-mr-1" />
          Saved but not in ERP yet — use Update in ERP.
          {createOnlyChanges.length > 0 && (
            <>
              {" "}<span className="tw-inline-block tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-[#f0b45a] tw-mx-1" />
              Dimensions and tax groups are set only when the journal is created — book any correction with an adjusting entry in AX.
            </>
          )}
        </p>
      )}

      {(section.history || []).length > 0 && (
        <div className="tw-pt-2">
          <p className="payment-journal-section-title">Replaced journals (before create-once)</p>
          {section.history.map((h) => (
            <p key={h.journal_num} className="tw-m-0 tw-text-[13px] tw-text-geist-fg-2">
              <span className="tw-line-through">{h.journal_num}</span> · {h.by} · {String(h.deleted_at || "").replace("T", " ").slice(0, 16)} · {h.reason}
            </p>
          ))}
        </div>
      )}
    </section>
  );
};

JournalSection.propTypes = {
  section: PropTypes.object.isRequired,
  lines: PropTypes.array.isRequired,
  values: PropTypes.object.isRequired,
  requestId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  accountTypes: PropTypes.array.isRequired,
  taxGroups: PropTypes.array.isRequired,
  createOnly: PropTypes.array.isRequired,
  busy: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onAddLine: PropTypes.func.isRequired,
  onRemoveLine: PropTypes.func.isRequired,
  // This journal's own header form, shown under the title.
  header: PropTypes.node,
  // Shown after the journal lines (payment details of an HSBC online payment).
  footer: PropTypes.node,
  counterparty: PropTypes.object,
  // Built lines taken out of the journal (draft only), with a way back.
  removedLines: PropTypes.array,
  onRestoreLine: PropTypes.func,
};

export default JournalSection;
